/**
 * /invite/:token — Cursor-style accept page (design 021). Public route: shows
 * the invite preview signed-out and bounces through /login; accepting requires
 * a session. Bearer semantics — whoever holds the link and is signed in joins.
 */

import { useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { authClient } from "@/lib/authClient";
import { api } from "../../convex/_generated/api";

function statusMessage(status: "accepted" | "revoked" | "expired"): string {
	switch (status) {
		case "accepted":
			return "This invite has already been used.";
		case "revoked":
			return "This invite was revoked by the firm.";
		case "expired":
			return "This invite has expired — ask your firm to send a new one.";
	}
}

export default function InviteAcceptPage() {
	const { token } = useParams<{ token: string }>();
	const navigate = useNavigate();
	const session = authClient.useSession();
	const preview = useQuery(
		api.members.previewInvite,
		token ? { token } : "skip",
	);
	const acceptInvite = useMutation(api.members.acceptInvite);
	const [accepting, setAccepting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleAccept() {
		if (!token) return;
		setAccepting(true);
		setError(null);
		try {
			const result = await acceptInvite({ token });
			if (result.authOrgId) {
				await authClient.organization.setActive({
					organizationId: result.authOrgId,
				});
			}
			// Full reload so session/org state resolves fresh in the workspace.
			window.location.href = "/workspace/members";
		} catch (err) {
			const data =
				err instanceof ConvexError
					? (err.data as { message?: string } | undefined)
					: undefined;
			setError(data?.message ?? "Something went wrong. Please try again.");
			setAccepting(false);
		}
	}

	const signedIn = !!session.data?.user;
	const email = session.data?.user?.email;

	return (
		<div className="flex min-h-screen items-center justify-center bg-background p-6">
			<Card className="w-full max-w-md">
				<CardContent className="flex flex-col gap-5 p-8 text-center">
					{preview === undefined ? (
						<div className="space-y-3">
							<Skeleton className="mx-auto h-8 w-2/3" />
							<Skeleton className="mx-auto h-4 w-full" />
							<Skeleton className="mx-auto h-10 w-full" />
						</div>
					) : preview === null ? (
						<>
							<h1 className="text-2xl font-semibold">Invite not found</h1>
							<p className="text-sm text-muted-foreground">
								This invite link isn't valid. Check the link or ask your firm to
								send a new one.
							</p>
						</>
					) : preview.status !== "pending" ? (
						<>
							<h1 className="text-2xl font-semibold">Accept Invite</h1>
							<p className="text-sm text-muted-foreground">
								{statusMessage(preview.status)}
							</p>
						</>
					) : (
						<>
							<h1 className="text-2xl font-semibold">Accept Invite</h1>
							<p className="text-sm text-muted-foreground">
								{preview.inviterName} invited you to join{" "}
								<strong>{preview.firmName}</strong> on Clerical.
							</p>
							<div className="rounded-lg border bg-muted/40 px-4 py-3 text-left">
								<div className="flex items-baseline justify-between">
									<span className="font-medium">{preview.firmName}</span>
									<span className="text-xs text-muted-foreground">
										{preview.memberCount} member
										{preview.memberCount === 1 ? "" : "s"}
									</span>
								</div>
							</div>
							{signedIn ? (
								<>
									<p className="text-xs text-muted-foreground">
										You're currently signed in as <strong>{email}</strong>
									</p>
									<Button
										onClick={() => void handleAccept()}
										disabled={accepting}
									>
										{accepting ? "Joining…" : "Accept Invite"}
									</Button>
									<Button
										variant="outline"
										onClick={() => {
											void authClient.signOut();
										}}
									>
										Sign in with a different account
									</Button>
								</>
							) : (
								<Button
									onClick={() =>
										navigate(
											`/login?redirect=${encodeURIComponent(`/invite/${token}`)}`,
										)
									}
								>
									Sign in to accept
								</Button>
							)}
							{error && <p className="text-sm text-destructive">{error}</p>}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
